import { Content } from "pdfmake/interfaces";
import { filter } from "report/common/icon";
import { formatKM } from "helpers/helpers";
import dayjs from "dayjs";

const AppliedFilter = (t: any, locationsFilterInfo: any[]): Content => {
  const containerWidth = 240;

  const isNumericStr = (s: string) => {
    const t = String(s).trim();
    if (t === "") return false;
    const n = Number(t);
    return Number.isFinite(n);
  };

  const toMillis = (s: any) => {
    // 统一从字符串/数字转换为“毫秒时间戳”
    if (s == null) return null;
    const t = String(s).trim();
    if (t === "") return null;

    const n = Number(t);
    if (!Number.isFinite(n)) return null;

    // 10位（秒） vs 13位（毫秒）通用处理：
    // 经验阈值：小于 1e12 基本可判断为秒级 Unix 时间戳
    const ms = Math.abs(n) < 1e12 ? n * 1000 : n;

    // 取整，避免小数秒/毫秒导致的非整数时间
    const msInt = Math.round(ms);
    return Number.isFinite(msInt) ? msInt : null;
  };

  const formatDate = (ms: any) => dayjs(ms).format("YYYY-MM-DD");

  const handleLabel = (filterData: any) => {
    let label = "";
    if (filterData.title === "topLocations") {
      label =
        filterData.value === "allLocations"
          ? t("locationSet.filterModal.allLocations")
          : t("locationSet.filterModal.topLocationsData", { data: filterData.value });
    } else {
      label = t("locationSet.filterModal." + filterData.title) + ": ";
      if (filterData.type === "select") {
        if (filterData.title === "occupancy") {
          label += filterData.value
            .map((item: string) => t("occupancyOptions." + item))
            .join(",");
        } else {
          label += filterData.value.join(",");
        }
      } else if (filterData.type === "number") {
        const minRaw = (filterData.min || "").trim();
        const maxRaw = (filterData.max || "").trim();

        const minValid = isNumericStr(minRaw);
        const maxValid = isNumericStr(maxRaw);

        // 仅 min 有效
        if (minValid && !maxValid) {
          label += `>${formatKM(minRaw)}`;
        }

        // 仅 max 有效
        if (!minValid && maxValid) {
          label += `<${formatKM(maxRaw)}`;
        }

        // 两者都无效
        if (minValid && maxValid) {
          label += `${formatKM(minRaw)}-${formatKM(maxRaw)}`;
        }
      } else if (filterData.type === "time") {
        const minMs = toMillis(filterData.min);
        const maxMs = toMillis(filterData.max);

        const minValid = minMs !== null;
        const maxValid = maxMs !== null;

        if (minValid && !maxValid) {
          label += `>${formatDate(minMs)}`;
        }
        if (!minValid && maxValid) {
          label += `<${formatDate(maxMs)}`;
        }

        // 两者都有效（不纠正顺序）
        if (minValid && maxValid) {
          label += `${formatDate(minMs)}-${formatDate(maxMs)}`;
        }
      }
    }

    return label;
  };

  // 你的所有胶囊文本（可很多）
  let labels: any = [];
  locationsFilterInfo.forEach((item) => {
    if (item.children) {
      item.children.forEach((child: any) => {
        if (child.value?.length || child.min || child.max) {
          labels.push(handleLabel(child));
        }
      });
    } else if (item.value) {
      labels.push(handleLabel(item));
    }
  });

  // 字符宽度表（相对 fontSize 的系数）
  // 可根据字体微调；这是常用字体的经验值，准确度很高
  const charWidthMap: any = {
    // 英文窄字符
    ".": 0.28,
    ",": 0.28,
    ":": 0.28,
    ";": 0.28,
    i: 0.32,
    l: 0.32,
    I: 0.38,
    "!": 0.3,
    "|": 0.3,

    // 英文中等宽度
    a: 0.56,
    c: 0.56,
    e: 0.56,
    o: 0.56,
    r: 0.4,
    s: 0.5,
    t: 0.38,

    // 英文宽字符
    m: 0.9,
    w: 0.88,
    M: 1.05,
    W: 1.05,

    // 数字
    "0": 0.6,
    "1": 0.52,
    "2": 0.6,
    "3": 0.6,
    "4": 0.6,
    "5": 0.6,
    "6": 0.6,
    "7": 0.6,
    "8": 0.6,
    "9": 0.6,

    // 空格
    " ": 0.33
  };

  // 默认宽度（英文平均值）
  const DEFAULT_LATIN_RATIO = 0.6;

  // 中文等宽字符（全宽字符）
  const CJK_RATIO = 1.1;

  // 判断是否为中文/日文/韩文（全宽字符）
  function isCJK(char: string) {
    return /[\u4e00-\u9fa5\u3040-\u30ff\uac00-\ud7af]/.test(char);
  }

  function measureText(label: string, fontSize: number) {
    let width = 0;
    for (const ch of label) {
      if (isCJK(ch)) {
        width += fontSize * CJK_RATIO;
      } else {
        // 英文 / 数字 / 符号
        width += fontSize * (charWidthMap[ch] ?? DEFAULT_LATIN_RATIO);
      }
    }
    return width;
  }

  const pillSvg = (
    label: string,
    {
      bg = "#E4E4E4",
      textColor = "#0009",
      fontSize = 9,
      padX = 8,
      padY = 4,
      radius = 10 // 胶囊效果
    } = {}
  ) => {
    const textW = measureText(label, fontSize);
    let mintextW = textW < 15 ? 15 : textW;
    const w = Math.ceil(mintextW + padX * 2);
    const h = Math.ceil(fontSize + padY * 2);

    // 文本垂直居中
    const yText = padY + fontSize * 0.85;

    return {
      svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
                <rect x="0" y="0" width="${w}" height="${h}" rx="${radius}" ry="${radius}" fill="${bg}"/>
                <text x="${padX}" y="${yText}" font-size="${fontSize}" fill="${textColor}">
                    ${escapeXml(label)}
                </text>
                </svg>
            `,
      width: w,
      height: h
    };
  };

  // 简单的 XML 转义，避免标签内容里有 &、< 等字符导致 SVG 解析问题
  const escapeXml = (s = "") => {
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  };

  // 把一串胶囊按容器宽度换行（flow 布局）
  const wrapPills = (
    labels: string[],
    wrapWidth: number,
    gap = 8,
    pillStyle = {}
  ) => {
    const rows = [];
    let row: any = [];
    let used = 0;
    labels.forEach((label) => {
      const pill = pillSvg(label, pillStyle);
      const pillW = pill.width;

      const nextUsed = row.length === 0 ? pillW : used + gap + pillW;
      if (nextUsed <= wrapWidth) {
        row.push(pill);
        used = nextUsed;
      } else {
        if (row.length) rows.push(row);
        row = [pill];
        used = pillW;
      }
    });
    if (row.length) rows.push(row);
    return rows;
  };

  // 生成按行排布的胶囊（会自动换行）
  const lines = wrapPills(labels, containerWidth, 8, {
    bg: "#E4E4E4", // 主胶囊背景
    textColor: "#0009",
    fontSize: 9
  });

  return (
    labels.length && {
      margin: [0, 20, 0, 0], // 顶部 50pt
      stack: [
        // 标题行（漏斗图标 + 文字）
        {
          columns: [
            { image: filter, width: 20 }, // 注意：这里不要再加 width:'auto'
            { text: t("report.Applied filters"), opacity: 0.6, marginTop: 4 }
          ],
          columnGap: 4,
          margin: [0, 0, 0, 8]
        },

        // 胶囊行：逐行渲染（自动换行的结果）
        ...lines.map((row, idx) => ({
          columns: row.map(
            (p: { svg: string; width: number }) =>
              ({ svg: p.svg, width: p.width } as Content)
          ), // 每个胶囊是一段 svg
          columnGap: 8,
          margin: [0, 0, 0, idx === lines.length - 1 ? 0 : 6] as [
            number,
            number,
            number,
            number
          ]
        }))
      ]
    }
  );
};

export default AppliedFilter;
